

// EventPropertiesModal.jsx - Fixed Version
import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    ListItem,
    ListItemText,
    Chip,
    CircularProgress,
    Typography,
    Paper,
} from "@mui/material";
import CloseIcon from "@material-ui/icons/Close";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import { toast } from "react-toastify";
import { BACKEND_BASE_URL } from "../config";
import CorrectionDictonaryAudioDialog from "./CorrectionDictonaryAudioDialog";

const API_AUDIO_LIST = (eventId) => `${BACKEND_BASE_URL}/api/capture/audio/list/${eventId}/`;
const API_AUDIO_PROCESS = (pk) => `${BACKEND_BASE_URL}/api/capture/audio/process/${pk}/`;
const API_TRANSCRIPT_DETAIL = (eventId, mediaId) =>
    `${BACKEND_BASE_URL}/api/capture/transcripts/${eventId}/${mediaId}/`;
const API_AUDIO_SUMMARY_GENERATE = `${BACKEND_BASE_URL}/api/capture/audio/summary/generate/`;

const getAudioUrl = (file) => {
    if (!file) return "";
    if (file.startsWith("/")) {
        return `${BACKEND_BASE_URL.replace(/\/$/, "")}${file}`;
    }
    return file;
};

const EventPropertiesModal = ({ open, onClose, event, fetchEvents, getToken }) => {
    const [audioList, setAudioList] = useState([]);
    const [selectedAudioIndex, setSelectedAudioIndex] = useState(null);
    const [isPlayingIndex, setIsPlayingIndex] = useState(null);
    const [loadingAudioList, setLoadingAudioList] = useState(false); // ✅ NEW
    const [audioLoading, setAudioLoading] = useState(false);
    // maps keyed by audio id
    const [transcriptLoadingMap, setTranscriptLoadingMap] = useState({});
    const [transcriptPollingMap, setTranscriptPollingMap] = useState({});
    const [summaryLoadingMap, setSummaryLoadingMap] = useState({});
    const [summariesMap, setSummariesMap] = useState({});

    const [transcriptModalOpen, setTranscriptModalOpen] = useState(false);
    const [transcriptText, setTranscriptText] = useState("");
    const [loadingTranscriptText, setLoadingTranscriptText] = useState(false);

    // Summary modal state
    const [summaryModalOpen, setSummaryModalOpen] = useState(false);
    const [currentSummaries, setCurrentSummaries] = useState([]);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [confirmDialogMessage, setConfirmDialogMessage] = useState("");
    const [confirmDialogAction, setConfirmDialogAction] = useState(() => () => { });
    const [deleteLoading, setDeleteLoading] = useState(null);
    const [correctionsOpen, setCorrectionsOpen] = useState(false);


    const audioRef = useRef();
    const [loadingAudioIndex, setLoadingAudioIndex] = useState(null);
    const shouldAutoPlayRef = useRef(false); // Track if we should auto-play after loading
    const [currentAudioUrl, setCurrentAudioUrl] = useState(null); // Store current audio URL for direct src setting

    const getHeaders = () => ({ Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" });

    useEffect(() => {
        if (event && open) {
            fetchAudioFiles(event.id);
        } else {
            // when modal closes, reset audio and state
            setAudioList([]);
            setSelectedAudioIndex(null);
            setIsPlayingIndex(null);
            setTranscriptLoadingMap({});
            setSummaryLoadingMap({});
            setSummariesMap({});
            setTranscriptPollingMap({});
            setLoadingAudioList(false); // ✅ RESET
            setAudioLoading(false);
            setCurrentAudioUrl(null); // Reset current audio URL
            shouldAutoPlayRef.current = false; // Reset auto-play flag
        }
        // eslint-disable-next-line
    }, [event, open]);

    // Set current audio URL when selectedAudioIndex changes
    useEffect(() => {
        if (selectedAudioIndex !== null && audioList[selectedAudioIndex]) {
            const url = getAudioUrl(audioList[selectedAudioIndex].file);
            if (url) {
                setCurrentAudioUrl(url);
            } else {
                setCurrentAudioUrl(null);
            }
        } else {
            setCurrentAudioUrl(null);
        }
    }, [selectedAudioIndex, audioList]);

    // Load audio metadata when currentAudioUrl changes (enables scrubbing)
    useEffect(() => {
        if (currentAudioUrl && selectedAudioIndex !== null) {
            const url = currentAudioUrl;

            // Wait for audio element to be rendered (since it's conditionally rendered)
            let retryCount = 0;
            const maxRetries = 20; // Max 1 second wait (20 * 50ms)
            let isCancelled = false; // Track if this effect was cancelled
            let metadataHandler = null;
            let canPlayHandler = null;
            let timeoutId = null;
            
            const setupAudio = () => {
                if (isCancelled) return; // Don't proceed if cancelled
                
                if (!audioRef.current) {
                    retryCount++;
                    if (retryCount < maxRetries) {
                        // Audio element not rendered yet, try again
                        setTimeout(setupAudio, 50);
                    } else {
                        console.warn("Audio element not found after retries");
                        setAudioLoading(false);
                        shouldAutoPlayRef.current = false;
                    }
                    return;
                }

                // Check if we need to update the src
                // Get the current src and normalize it for comparison
                const currentSrc = audioRef.current.src || "";
                // Extract just the pathname for comparison (ignore protocol, domain, query params)
                const getPathFromUrl = (urlStr) => {
                    try {
                        if (!urlStr) return "";
                        const urlObj = new URL(urlStr);
                        return urlObj.pathname;
                    } catch {
                        // If URL parsing fails, try to extract path manually
                        const match = urlStr.match(/\/[^?#]*/);
                        return match ? match[0] : urlStr.split('?')[0].split('#')[0];
                    }
                };
                
                const currentPath = getPathFromUrl(currentSrc);
                const newPath = getPathFromUrl(url);
                
                // Always update src to ensure it's set (even if paths match, force reload for metadata)
                // Set src and preload auto to enable full scrubbing
                const needsUpdate = !currentSrc || currentPath !== newPath;
                
                // Stop previous audio before loading new one (but don't reset currentTime if same audio)
                // IMPORTANT: Don't interfere if user is currently seeking
                if (audioRef.current) {
                    // If user is actively seeking, don't interfere
                    if (audioRef.current.seeking) {
                        return;
                    }
                    
                    const wasPlaying = !audioRef.current.paused;
                    audioRef.current.pause();
                    // Only reset currentTime if switching to a different audio file
                    if (needsUpdate) {
                        audioRef.current.currentTime = 0;
                    }
                    // If same audio and was playing, we'll resume after metadata loads
                }
                // Set up metadata handlers (needed for both new and existing audio)
                let timeoutFired = false; // Track if timeout fired before metadata loaded
                metadataHandler = () => {
                    if (audioRef.current && !isCancelled) {
                        if (metadataHandler) {
                            audioRef.current.removeEventListener("loadedmetadata", metadataHandler);
                        }
                        
                        
                        setAudioLoading(false);
                        
                        // Clear timeout since metadata loaded
                        if (timeoutId) {
                            clearTimeout(timeoutId);
                            timeoutId = null;
                        }
                        
                        // If auto-play was requested, play after metadata is loaded
                        if (shouldAutoPlayRef.current) {
                            const playPromise = audioRef.current.play();
                            if (playPromise !== undefined) {
                                playPromise
                                    .then(() => {
                                        if (!isCancelled) {
                                            setIsPlayingIndex(selectedAudioIndex);
                                            shouldAutoPlayRef.current = false;
                                        }
                                    })
                                    .catch((err) => {
                                        if (!isCancelled) {
                                            console.error("Auto-play failed:", err);
                                            shouldAutoPlayRef.current = false;
                                            toast.error("Failed to play audio. Please click play again.");
                                        }
                                    });
                            } else {
                                if (!isCancelled) {
                                    setIsPlayingIndex(selectedAudioIndex);
                                    shouldAutoPlayRef.current = false;
                            }
                        }
                    }
                    }
                };
                    
                // Also handle canplay event as fallback
                canPlayHandler = () => {
                        if (audioRef.current && !isCancelled) {
                        // Only set loading to false if metadata is loaded
                        if (audioRef.current.readyState >= 2 && audioRef.current.duration && !isNaN(audioRef.current.duration)) {
                            if (canPlayHandler) {
                                audioRef.current.removeEventListener("canplay", canPlayHandler);
                            }
                            setAudioLoading(false);
                            
                            // Clear timeout since can play
                            if (timeoutId) {
                                clearTimeout(timeoutId);
                                timeoutId = null;
                            }
                        }
                    }
                };
                
                if (needsUpdate) {
                    audioRef.current.src = url;
                    audioRef.current.preload = "auto";
                    // Explicitly call load() to ensure metadata is loaded for scrubbing
                    audioRef.current.load();
                    setAudioLoading(true);
                    
                    // Add event listeners for metadata loading
                    audioRef.current.addEventListener("loadedmetadata", metadataHandler);
                    audioRef.current.addEventListener("canplay", canPlayHandler);
                    
                    // Set a timeout to ensure we don't wait forever
                    // Increased to 20 seconds to allow for slower network connections
                    timeoutId = setTimeout(() => {
                        if (audioRef.current && !isCancelled) {
                            // Check if metadata loaded but event didn't fire
                            if (audioRef.current.readyState >= 2 && audioRef.current.duration && !isNaN(audioRef.current.duration)) {
                                // Metadata actually loaded, just clear handlers
                                setAudioLoading(false);
                                if (metadataHandler) {
                                    audioRef.current.removeEventListener("loadedmetadata", metadataHandler);
                                }
                                if (canPlayHandler) {
                                    audioRef.current.removeEventListener("canplay", canPlayHandler);
                                }
                                
                                // If auto-play was requested
                            if (shouldAutoPlayRef.current) {
                                const playPromise = audioRef.current.play();
                                if (playPromise !== undefined) {
                                    playPromise
                                        .then(() => {
                                            if (!isCancelled) {
                                                setIsPlayingIndex(selectedAudioIndex);
                                                shouldAutoPlayRef.current = false;
                                            }
                                        })
                                        .catch((err) => {
                                            if (!isCancelled) {
                                                console.error("Auto-play failed:", err);
                                                shouldAutoPlayRef.current = false;
                                            }
                                        });
                                } else {
                                    if (!isCancelled) {
                                        setIsPlayingIndex(selectedAudioIndex);
                                        shouldAutoPlayRef.current = false;
                                    }
                                }
                            }
                            } else {
                                // Metadata still not loaded after timeout - but don't give up yet
                                // The metadata handler might still fire, so we'll just log a warning
                                // but keep the handlers attached for a bit longer
                                console.warn("Audio metadata not loaded after 20 seconds - still waiting...");
                                console.warn("This may indicate slow network or large file. Metadata may still load.");
                                
                                // Set a final timeout to give up completely after 30 more seconds
                                setTimeout(() => {
                        if (audioRef.current && !isCancelled) {
                                        if (audioRef.current.readyState < 2 || !audioRef.current.duration || isNaN(audioRef.current.duration)) {
                                            console.error("Audio metadata failed to load after extended timeout. Scrubbing may not work.");
                                            setAudioLoading(false);
                                            shouldAutoPlayRef.current = false;
                                            // Remove handlers to prevent further attempts
                                            if (metadataHandler && audioRef.current) {
                                                audioRef.current.removeEventListener("loadedmetadata", metadataHandler);
                                            }
                                            if (canPlayHandler && audioRef.current) {
                                    audioRef.current.removeEventListener("canplay", canPlayHandler);
                                }
                                        }
                                    }
                                }, 30000); // Additional 30 seconds
                            }
                                    timeoutId = null;
                                }
                    }, 20000); // Increased to 20 second timeout
                } else {
                    // If src is the same, check if metadata is already loaded
                    if (audioRef.current.readyState >= 2 && audioRef.current.duration && !isNaN(audioRef.current.duration)) {
                        setAudioLoading(false);
                        // If auto-play was requested and audio is ready
                        if (shouldAutoPlayRef.current && audioRef.current.paused) {
                            const playPromise = audioRef.current.play();
                            if (playPromise !== undefined) {
                                playPromise
                                    .then(() => {
                                        if (!isCancelled) {
                                            setIsPlayingIndex(selectedAudioIndex);
                                            shouldAutoPlayRef.current = false;
                                        }
                                    })
                                    .catch((err) => {
                                        if (!isCancelled) {
                                            console.error("Auto-play failed:", err);
                                            shouldAutoPlayRef.current = false;
                                        }
                                    });
                            }
                        }
                        return; // Metadata already loaded, no need to set up listeners
                    } else {
                        // Same src but metadata not loaded, force reload
                        audioRef.current.load();
                        setAudioLoading(true);
                        
                        // Add event listeners for metadata loading
                        audioRef.current.addEventListener("loadedmetadata", metadataHandler);
                        audioRef.current.addEventListener("canplay", canPlayHandler);
                        
                        // Set a timeout to ensure we don't wait forever
                        // Increased to 20 seconds to allow for slower network connections
                        timeoutId = setTimeout(() => {
                            if (audioRef.current && !isCancelled) {
                                // Check if metadata loaded but event didn't fire
                                if (audioRef.current.readyState >= 2 && audioRef.current.duration && !isNaN(audioRef.current.duration)) {
                                    // Metadata actually loaded, just clear handlers
                                    setAudioLoading(false);
                                    if (metadataHandler) {
                                        audioRef.current.removeEventListener("loadedmetadata", metadataHandler);
                                    }
                                    if (canPlayHandler) {
                                        audioRef.current.removeEventListener("canplay", canPlayHandler);
                                    }
                                    
                                    // If auto-play was requested
                                    if (shouldAutoPlayRef.current) {
                                        const playPromise = audioRef.current.play();
                                        if (playPromise !== undefined) {
                                            playPromise
                                                .then(() => {
                                                    if (!isCancelled) {
                                                        setIsPlayingIndex(selectedAudioIndex);
                                                        shouldAutoPlayRef.current = false;
                                                    }
                                                })
                                                .catch((err) => {
                                                    if (!isCancelled) {
                                                        console.error("Auto-play failed:", err);
                                                        shouldAutoPlayRef.current = false;
                                                    }
                                                });
                                        } else {
                                            if (!isCancelled) {
                                                setIsPlayingIndex(selectedAudioIndex);
                                                shouldAutoPlayRef.current = false;
                                            }
                                        }
                                    }
                                } else {
                                    // Metadata still not loaded after timeout - but don't give up yet
                                    // The metadata handler might still fire, so we'll just log a warning
                                    // but keep the handlers attached for a bit longer
                                    timeoutFired = true; // Mark that timeout fired
                                    console.warn("Audio metadata not loaded after 20 seconds - still waiting...");
                                    console.warn("This may indicate slow network or large file. Metadata may still load.");
                                    
                                    // Set a final timeout to give up completely after 30 more seconds
                                    setTimeout(() => {
                                        if (audioRef.current && !isCancelled) {
                                            if (audioRef.current.readyState < 2 || !audioRef.current.duration || isNaN(audioRef.current.duration)) {
                                                console.error("Audio metadata failed to load after extended timeout. Scrubbing may not work.");
                                    setAudioLoading(false);
                                    shouldAutoPlayRef.current = false;
                                                // Remove handlers to prevent further attempts
                                                if (metadataHandler && audioRef.current) {
                                                    audioRef.current.removeEventListener("loadedmetadata", metadataHandler);
                                                }
                                                if (canPlayHandler && audioRef.current) {
                                                    audioRef.current.removeEventListener("canplay", canPlayHandler);
                                                }
                                            }
                                        }
                                    }, 30000); // Additional 30 seconds
                                }
                                timeoutId = null;
                            }
                        }, 20000); // Increased to 20 second timeout
                    }
                }
            };

            // Start setup after React has rendered the audio element
            // Use requestAnimationFrame to wait for next paint, then check for element
            requestAnimationFrame(() => {
                setTimeout(setupAudio, 50);
            });
            
            // Cleanup function
            return () => {
                isCancelled = true;
                // Clear timeout if it exists
                if (timeoutId) {
                    clearTimeout(timeoutId);
                    timeoutId = null;
                }
                // Remove event listeners
                if (audioRef.current) {
                    if (metadataHandler) {
                        audioRef.current.removeEventListener("loadedmetadata", metadataHandler);
                    }
                    if (canPlayHandler) {
                        audioRef.current.removeEventListener("canplay", canPlayHandler);
                    }
                    // Stop audio if switching away
                    audioRef.current.pause();
                    audioRef.current.currentTime = 0;
                }
            };
        }
    }, [currentAudioUrl, selectedAudioIndex]);

    useEffect(() => {
        const audioEl = audioRef.current;
        if (!audioEl) return;

        const handlePlay = () => {
            setIsPlayingIndex(selectedAudioIndex); // sync state
        };

        const handlePause = () => {
            setIsPlayingIndex(null); // sync state
        };

        const handleLoadedMetadata = () => {
            setAudioLoading(false);
        };

        const handleCanPlay = () => {
            setAudioLoading(false);
        };

        const handleCanPlayThrough = () => {
            setAudioLoading(false);
        };

        const handleLoadStart = () => {
            setAudioLoading(true);
        };

        const handleError = (e) => {
            console.error("Audio error:", e);
            setAudioLoading(false);
            toast.error("Error loading audio file.");
            shouldAutoPlayRef.current = false;
        };

        audioEl.addEventListener("play", handlePlay);
        audioEl.addEventListener("pause", handlePause);
        audioEl.addEventListener("loadedmetadata", handleLoadedMetadata);
        audioEl.addEventListener("canplay", handleCanPlay);
        audioEl.addEventListener("canplaythrough", handleCanPlayThrough);
        audioEl.addEventListener("loadstart", handleLoadStart);
        audioEl.addEventListener("error", handleError);

        return () => {
            audioEl.removeEventListener("play", handlePlay);
            audioEl.removeEventListener("pause", handlePause);
            audioEl.removeEventListener("loadedmetadata", handleLoadedMetadata);
            audioEl.removeEventListener("canplay", handleCanPlay);
            audioEl.removeEventListener("canplaythrough", handleCanPlayThrough);
            audioEl.removeEventListener("loadstart", handleLoadStart);
            audioEl.removeEventListener("error", handleError);
        };
    }, [selectedAudioIndex]);

    // Ensure metadata loads when audio element has src
    useEffect(() => {
        if (audioRef.current && currentAudioUrl) {
            // Check if src is already set and matches
            const currentSrc = audioRef.current.src || "";
            if (currentSrc && currentSrc.includes(currentAudioUrl.split('/').pop())) {
                // Src is set, ensure metadata loads
                if (audioRef.current.readyState < 2) {
                    // Metadata not loaded yet, force load
                    setAudioLoading(true);
                    audioRef.current.load();
                } else if (audioRef.current.duration && !isNaN(audioRef.current.duration)) {
                    // Metadata already loaded
                    setAudioLoading(false);
                }
            } else {
                // Src doesn't match, it will be set by the other useEffect
                // But ensure we're in loading state
                setAudioLoading(true);
            }
        }
    }, [currentAudioUrl]);

    const fetchAudioFiles = async (eventId) => {
        setLoadingAudioList(true); // ✅ START LOADING
        try {
            const res = await fetch(API_AUDIO_LIST(eventId), { headers: getHeaders() });
            if (!res.ok) {
                toast.error("Failed to fetch audio files.");
                setAudioList([]);
                return;
            }
            const data = await res.json();
            const list = Array.isArray(data.data) ? data.data : Array.isArray(data) ? data : [];
            setAudioList(list);
        } catch (err) {
            console.error(err);
            toast.error("Error fetching audio files.");
            setAudioList([]);
        } finally {
            setLoadingAudioList(false); // ✅ STOP LOADING
        }
    };

    const playAudio = (index) => {
        if (!audioList[index]) return;
        const url = getAudioUrl(audioList[index].file);
        if (!url) {
            toast.error("Audio URL missing.");
            return;
        }

        // If clicking a different audio, switch to it first
        if (selectedAudioIndex !== index) {
            setSelectedAudioIndex(index);
            setIsPlayingIndex(null);
            setAudioLoading(true);
            shouldAutoPlayRef.current = true; // Mark that we want to auto-play after loading
            // The useEffect will handle loading and auto-playing once the element is rendered
            return;
        }

        // From here, we know selectedAudioIndex === index, so audio element should exist
        // If element not ready yet, the useEffect will handle it when it renders
        // Just set the auto-play flag and let useEffect do the work
        if (!audioRef.current) {
            // Element not rendered yet, but selectedAudioIndex is already set
            // The useEffect will handle loading and playing when element is ready
            shouldAutoPlayRef.current = true;
            setAudioLoading(true);
            return;
        }

        // If clicking on currently playing audio, just pause it
        if (isPlayingIndex === index && !audioRef.current.paused) {
            audioRef.current.pause();
            setIsPlayingIndex(null);
            shouldAutoPlayRef.current = false;
            return;
        }

        // Same audio selected, just play/pause
        if (audioRef.current.paused) {
            // Check if metadata is loaded
            if (audioRef.current.readyState >= 2) {
                const playPromise = audioRef.current.play();
                if (playPromise !== undefined) {
                    playPromise
                        .then(() => {
                            setIsPlayingIndex(index);
                            setAudioLoading(false);
                        })
                        .catch((err) => {
                            console.error("Play failed:", err);
                            setAudioLoading(false);
                            toast.error("Failed to play audio. Please try again.");
                        });
                } else {
                    setIsPlayingIndex(index);
                    setAudioLoading(false);
                }
            } else {
                // Metadata not loaded yet, wait for it
                setAudioLoading(true);
                shouldAutoPlayRef.current = true;
                const onMetadataLoaded = () => {
                    audioRef.current.removeEventListener("loadedmetadata", onMetadataLoaded);
                    const playPromise = audioRef.current.play();
                    if (playPromise !== undefined) {
                        playPromise
                            .then(() => {
                                setIsPlayingIndex(index);
                                setAudioLoading(false);
                                shouldAutoPlayRef.current = false;
                            })
                            .catch((err) => {
                                console.error("Play failed:", err);
                                setAudioLoading(false);
                                shouldAutoPlayRef.current = false;
                                toast.error("Failed to play audio.");
                            });
                    } else {
                        setIsPlayingIndex(index);
                        setAudioLoading(false);
                        shouldAutoPlayRef.current = false;
                    }
                };
                audioRef.current.addEventListener("loadedmetadata", onMetadataLoaded);
            }
        } else {
            audioRef.current.pause();
            setIsPlayingIndex(null);
        }
    };

    const handleAudioEnded = () => setIsPlayingIndex(null);

    // ---------- Transcript Flow ----------
    const generateTranscript = async (audio) => {
        if (!audio || !audio.id) return;
        const audioId = audio.id;

        if (transcriptLoadingMap[audioId]) {
            toast.info("Transcript is already processing. Waiting for completion...");
            startTranscriptPolling(audioId);
            return;
        }

        setTranscriptLoadingMap((m) => ({ ...m, [audioId]: true }));

        try {
            const res = await fetch(API_AUDIO_PROCESS(audioId), {
                method: "POST",
                headers: getHeaders(),
            });
            const data = await res.json();

            if (res.ok && (data.status === "started" || data.status === "processing")) {
                toast.info("Transcription started. We'll poll for status.");
                startTranscriptPolling(audioId);
            } else if (data.status === "exists") {
                toast.info("Transcript already exists; fetching it.");
                await fetchTranscriptAndUpdate(audio.event, audioId);
            } else {
                toast.error(data.message || "Failed to start transcription.");
            }
        } catch (err) {
            console.error(err);
            toast.error("Error initiating transcript.");
        } finally {
            setTranscriptLoadingMap((m) => ({ ...m, [audioId]: false }));
        }
    };

    const startTranscriptPolling = (audioId, interval = 3000, maxAttempts = 60) => {
        if (transcriptPollingMap[audioId]) return;

        setTranscriptPollingMap((m) => ({ ...m, [audioId]: true }));
        let attempts = 0;

        const poll = async () => {
            attempts++;
            try {
                const transcriptRes = await fetch(API_TRANSCRIPT_DETAIL(event.id, audioId), { headers: getHeaders() });
                if (transcriptRes.ok) {
                    const json = await transcriptRes.json();
                    const transcriptData = json.data || json;
                    if (transcriptData?.raw_text) {
                        await fetchAudioFiles(event.id);
                        setTranscriptPollingMap((m) => ({ ...m, [audioId]: false }));
                        toast.success("Transcript completed.");
                        return;
                    }
                }
            } catch (err) {
                console.error("transcript poll error", err);
            }
            if (attempts >= maxAttempts) {
                toast.warning("Transcript is taking longer than expected. It will complete in the background.");
                setTranscriptPollingMap((m) => ({ ...m, [audioId]: false }));
                return;
            }
            setTimeout(poll, interval);
        };
        poll();
    };

    const fetchTranscriptAndUpdate = async (eventId, audioId) => {
        setLoadingTranscriptText(true);
        try {
            const res = await fetch(API_TRANSCRIPT_DETAIL(eventId, audioId), { headers: getHeaders() });
            if (res.ok) {
                const data = await res.json();
                const txt = data.data?.raw_text || "No transcript found.";
                setTranscriptText(txt);
                setTranscriptModalOpen(true);
            } else {
                toast.error("Failed to fetch transcript.");
                setTranscriptText("");
            }
        } catch (err) {
            console.error(err);
            toast.error("Error fetching transcript.");
            setTranscriptText("");
        } finally {
            setLoadingTranscriptText(false);
        }
    };

    const viewTranscript = async (audio) => {
        if (!audio) return;
        await fetchTranscriptAndUpdate(event.id, audio.id);
    };

    // ---------- Summary Flow ----------
    const generateSummary = async (audio) => {
        if (!audio) return [];
        const audioId = audio.id;
        const transcript = audio.transcripts?.[0];
        if (!transcript) {
            toast.warning("No transcript available. Generate transcript first.");
            return [];
        }

        const transcriptId = transcript.id;
        setSummaryLoadingMap((m) => ({ ...m, [audioId]: true }));

        try {
            const res = await fetch(API_AUDIO_SUMMARY_GENERATE, {
                method: "POST",
                headers: getHeaders(),
                body: JSON.stringify({
                    capture_transcript_id: transcriptId,
                    event_id: event.id,
                    state: event?.state || "",
                    county: event?.county || "",
                    municipality: event?.municipality_name || "",
                }),
            });

            const data = await res.json();
            if (res.ok && data.status === "completed") {
                const summaries = data.summaries || [];
                setSummariesMap((m) => ({ ...m, [audioId]: summaries }));
                toast.success("Summary generated / fetched successfully.");
                fetchEvents?.();
                return summaries; // ✅ return the new summaries
            } else {
                toast.error(data.message || "Failed to generate summary");
                return [];
            }
        } catch (err) {
            console.error("summary generation error", err);
            toast.error("Error generating summary.");
            return [];
        } finally {
            setSummaryLoadingMap((m) => ({ ...m, [audioId]: false }));
        }
    };

    const viewSummary = async (audio) => {
        if (!audio) return;
        const audioId = audio.id;

        // If already cached
        if (summariesMap[audioId] && summariesMap[audioId].length > 0) {
            showSummariesDialog(summariesMap[audioId]);
            return;
        }

        // Otherwise generate or fetch
        const summaries = await generateSummary(audio);

        if (summaries && summaries.length > 0) {
            showSummariesDialog(summaries);  // ✅ use the latest summaries
        } else {
            toast.info("No summaries available.");
        }
    };


    const showSummariesDialog = (summaries) => {
        setCurrentSummaries(summaries);
        setSummaryModalOpen(true);
    };

    const getCategoryColor = (category) => {
        const colors = {
            'Public Health': '#4CAF50',
            'Education': '#2196F3',
            'Environment': '#8BC34A',
            'Infrastructure': '#FF9800',
            'Finance': '#9C27B0',
            'Urban Planning': '#00BCD4',
            'Governance': '#F44336',
            'Public Safety': '#795548',
            'Community': '#607D8B',
            'miscellaneous': '#9E9E9E'
        };
        return colors[category] || '#9E9E9E';
    };

    // ---------- Helpers: compute chip state ----------
    const computePipelineState = (audio) => {
        const transcript = audio.transcripts?.[0];
        const hasTranscript = !!transcript;
        const sttStatus = transcript?.stt_status || null;
        const summaryStatus = transcript?.summary_status || null;
        const hasSummaries = !!(summariesMap[audio.id] && summariesMap[audio.id].length > 0);

        return {
            uploaded: true,
            transcribing: sttStatus === "processing" || transcriptLoadingMap[audio.id] === true || (!!transcriptPollingMap[audio.id]),
            transcribed: sttStatus === "completed",
            summarizing: summaryLoadingMap[audio.id] === true || summaryStatus === "processing",
            summarized: hasSummaries || summaryStatus === "completed",
            published:
                transcript?.publish_status === "published" || transcript?.is_published === true || false,
        };
    };
    const showConfirmDialog = (message, onConfirm) => {
        setConfirmDialogMessage(message);
        setConfirmDialogAction(() => onConfirm);
        setConfirmDialogOpen(true);
    };

    const handleConfirmDialogClose = () => {
        setConfirmDialogOpen(false);
        setConfirmDialogMessage("");
        setConfirmDialogAction(() => () => { });
    };

    const handleConfirmDialogConfirm = () => {
        confirmDialogAction();
        handleConfirmDialogClose();
    };
    const handleDelete = (id) => {
        showConfirmDialog("Are you sure you want to delete this event?", () => handleDeleteConfirmed(id));
    };
    const API_DELETE_EVENT = (id) => `${BACKEND_BASE_URL}/api/capture/capture-events/delete/${id}/`;
    const handleDeleteConfirmed = async (id) => {
        setDeleteLoading(id);
        try {
            const res = await fetch(API_DELETE_EVENT(id), {
                method: "DELETE",
                headers: getHeaders(),
            });
            if (res.ok) {
                toast.success("Event deleted successfully.");
                fetchEvents?.(); // refresh list in parent
                onClose(); // close modal
            } else {
                toast.error("Failed to delete event.");
            }
        } catch (err) {
            console.error(err);
            toast.error("Error deleting event.");
        }
        setDeleteLoading(null);
    };
    const runCorrections = async () => {
        if (!event?.id) return;
        try {
            toast.info("Applying corrections to audio summaries...");
            const res = await fetch(`${BACKEND_BASE_URL}/api/capture/events/${event.id}/corrections/run/`, {
                method: "POST",
                headers: getHeaders(),
            });
            const data = await res.json();
            if (res.ok) {
                toast.success(data.message || "Corrections applied successfully!");
                fetchAudioFiles(event.id); // optional: refresh audio list if needed
            } else {
                toast.error(data.message || "Failed to apply corrections.");
            }
        } catch (err) {
            console.error(err);
            toast.error("Error applying corrections.");
        }
    };



    // ---------- Render ----------
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" PaperProps={{ style: { minHeight: "70vh" } }}>
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="h6">Properties - {event?.title}</Typography>
                <IconButton onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                {!event ? (
                    <Typography>No event selected.</Typography>
                ) : loadingAudioList ? (
                    // ✅ LOADING STATE
                    <Box textAlign="center" py={6}>
                        <CircularProgress size={50} />
                        <Typography variant="body1" sx={{ mt: 2 }}>
                            Loading audio files...
                        </Typography>
                    </Box>
                ) : audioList.length === 0 ? (
                    <Box textAlign="center" py={6}>
                        <Typography>No audio files available for this event.</Typography>
                    </Box>
                ) : (
                    audioList.map((audio, idx) => {
                        const pipeline = computePipelineState(audio);
                        return (
                            <Paper key={audio.id} sx={{ mb: 2, p: 2 }}>
                                <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                                    <Box flex="1">
                                        <ListItem disableGutters sx={{ p: 0 }}>
                                            <ListItemText
                                                primary={audio.caption || `Audio ${idx + 1}`}
                                                secondary={`Uploaded at: ${new Date(audio.uploaded_at).toLocaleString('en-US', {
                                                    month: '2-digit',
                                                    day: '2-digit',
                                                    year: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                })}`}
                                                primaryTypographyProps={{ fontWeight: "bold" }}

                                            />
                                        </ListItem>

                                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
                                            <Chip label="Uploaded" variant="outlined" size="small" />

                                            {pipeline.transcribing ? (
                                                <Chip label="Transcribing..." size="small" />
                                            ) : pipeline.transcribed ? (
                                                <Chip label="Transcribed" color="success" size="small" />
                                            ) : (
                                                <Chip label="Awaiting transcription" size="small" />
                                            )}

                                            {pipeline.summarizing ? (
                                                <Chip label="Summarizing..." size="small" />
                                            ) : pipeline.summarized ? (
                                                <Chip label="Summarized" color="success" size="small" />
                                            ) : (
                                                <Chip label="Summary pending" size="small" />
                                            )}

                                            {pipeline.published ? <Chip label="Published" color="primary" size="small" /> : null}
                                        </Box>

                                        {audio.transcripts?.[0]?.raw_text ? (
                                            <Typography variant="body2" sx={{ mt: 1, maxHeight: 80, overflow: "auto", whiteSpace: "pre-line" }}>
                                                {audio.transcripts[0].raw_text.slice(0, 300)}{audio.transcripts[0].raw_text.length > 300 ? "..." : ""}
                                            </Typography>
                                        ) : null}
                                    </Box>

                                    <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
                                        <Button
                                            variant={selectedAudioIndex === idx && isPlayingIndex === idx && !audioLoading ? "contained" : "outlined"}
                                            color={selectedAudioIndex === idx && isPlayingIndex === idx && !audioLoading ? "success" : "primary"}
                                            size="small"
                                            startIcon={selectedAudioIndex === idx && isPlayingIndex === idx && !audioLoading ? <PauseIcon /> : <PlayArrowIcon />}
                                            onClick={() => playAudio(idx)}
                                        >
                                            {selectedAudioIndex === idx && isPlayingIndex === idx && !audioLoading ? "Pause" : "Play"}
                                        </Button>

                                        {/* Transcript buttons */}
                                        {pipeline.transcribed ? (
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={() => viewTranscript(audio)}
                                                disabled={loadingTranscriptText}
                                            >
                                                View Transcript
                                            </Button>
                                        ) : (
                                            <Button
                                                variant="contained"
                                                size="small"
                                                onClick={() => generateTranscript(audio)}
                                                disabled={transcriptLoadingMap[audio.id] === true || pipeline.transcribing}
                                            >
                                                {transcriptLoadingMap[audio.id] ? <CircularProgress size={18} /> : "Generate Transcript"}
                                            </Button>
                                        )}

                                        {/* Summary buttons */}
                                        {pipeline.summarized ? (
                                            <Button variant="outlined" size="small" onClick={() => viewSummary(audio)}>
                                                View Summary
                                            </Button>
                                        ) : (
                                            <Button
                                                variant="contained"
                                                size="small"
                                                onClick={() => generateSummary(audio)}
                                                disabled={summaryLoadingMap[audio.id] === true || !pipeline.transcribed}
                                            >
                                                {summaryLoadingMap[audio.id] ? <CircularProgress size={18} /> : "Generate Summary"}
                                            </Button>
                                        )}

                                    </Box>
                                </Box>
                                {/* Audio player shown below the selected audio */}
                                {selectedAudioIndex === idx && (
                                    <Box sx={{ mt: 2 }}>
                                        <audio 
                                            ref={(el) => {
                                                audioRef.current = el;
                                                if (el && !el.dataset.scrubbingListenersAttached) {
                                                    el.dataset.scrubbingListenersAttached = 'true';
                                                    
                                                    // Add comprehensive scrubbing event listeners
                                                    // Track the time when seeking starts to detect if it changes
                                                    let seekingStartTime = null;
                                                    
                                                    const seekingHandler = (e) => {
                                                        const audio = e.target;
                                                        seekingStartTime = audio.currentTime;
                                                        
                                                        // Ensure audio can seek - check if metadata is loaded
                                                        if (!audio.duration || !isFinite(audio.duration)) {
                                                            toast.warn('Audio metadata not loaded yet. Please wait...');
                                                        } else if (audio.readyState < 1) {
                                                            toast.warn('Audio not ready for scrubbing yet. Please wait...');
                                                        }
                                                        
                                                        // Monitor currentTime changes during seeking
                                                        const monitorSeek = setInterval(() => {
                                                            if (!audio.seeking) {
                                                                clearInterval(monitorSeek);
                                                                return;
                                                            }
                                                            if (Math.abs(audio.currentTime - seekingStartTime) > 0.1) {
                                                                seekingStartTime = audio.currentTime; // Update start time
                                                            }
                                                        }, 50);
                                                    };
                                                    
                                                    const seekedHandler = (e) => {
                                                        const audio = e.target;
                                                        seekingStartTime = null;
                                                    };
                                                    
                                                    const timeUpdateHandler = (e) => {
                                                        // Handler for time updates during seeking
                                                    };
                                                    
                                                    
                                                    el.addEventListener('seeking', seekingHandler, true);
                                                    el.addEventListener('seeked', seekedHandler, true);
                                                    el.addEventListener('timeupdate', timeUpdateHandler, true);
                                                    
                                                    // Store handlers for cleanup
                                                    el._seekingHandler = seekingHandler;
                                                    el._seekedHandler = seekedHandler;
                                                    el._timeUpdateHandler = timeUpdateHandler;
                                                    
                                                    // Monitor seeking property with polling
                                                    let lastSeeking = el.seeking;
                                                    let lastTime = el.currentTime;
                                                    // Note: seekingStartTime is already declared above for event handlers
                                                    
                                                    const seekingMonitor = setInterval(() => {
                                                        if (!el.parentNode) {
                                                            clearInterval(seekingMonitor);
                                                            return;
                                                        }
                                                        
                                                        // Track when seeking starts
                                                        if (el.seeking && !lastSeeking) {
                                                            seekingStartTime = el.currentTime;
                                                        }
                                                        
                                                        // Track when seeking ends
                                                        if (!el.seeking && lastSeeking) {
                                                            seekingStartTime = null;
                                                        }
                                                        
                                                        lastSeeking = el.seeking;
                                                        lastTime = el.currentTime;
                                                    }, 50);
                                                    
                                                    el._seekingMonitor = seekingMonitor;
                                                }
                                            }}
                                            src={currentAudioUrl || undefined}
                                            onEnded={handleAudioEnded} 
                                            onCanPlay={() => {
                                                if (audioRef.current && audioRef.current.readyState >= 2 && audioRef.current.duration) {
                                                    setAudioLoading(false);
                                                }
                                            }}
                                            onLoadedMetadata={() => {
                                                setAudioLoading(false);
                                            }}
                                            onCanPlayThrough={() => setAudioLoading(false)}
                                            onLoadStart={() => setAudioLoading(true)}
                                            onError={(e) => {
                                                console.error("Audio error:", e);
                                                setAudioLoading(false);
                                                toast.error("Error loading audio file.");
                                                shouldAutoPlayRef.current = false;
                                            }}
                                            preload="auto"
                                            controls
                                            style={{ 
                                                width: "100%",
                                                pointerEvents: 'auto',
                                                zIndex: 10
                                            }} 
                                        />
                                        {audioLoading && (
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                                                <CircularProgress size={16} />
                                                <Typography variant="caption" color="textSecondary">
                                                    Loading audio...
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </Paper>
                        );
                    })
                )}
            </DialogContent>

            <DialogActions>
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setCorrectionsOpen(true)}
                >
                    Correction Dictionary
                </Button>
                <Button
                    variant="contained"
                    color="secondary"
                    onClick={runCorrections}
                >
                    Run Corrections
                </Button>
                <Button
                    color="error"
                    variant="contained"
                    onClick={() => handleDelete(event?.id)}
                    disabled={deleteLoading === event?.id}
                >
                    {deleteLoading === event?.id ? <CircularProgress size={20} /> : "Delete Event"}
                </Button>
            </DialogActions>
            <CorrectionDictonaryAudioDialog
                open={correctionsOpen}
                onClose={() => setCorrectionsOpen(false)}
                eventId={event?.id}
                getToken={getToken}
            />

            {/* Transcript viewer modal */}
            {transcriptModalOpen && (
                <Dialog open={transcriptModalOpen} onClose={() => setTranscriptModalOpen(false)} fullWidth maxWidth="md">
                    <DialogTitle>Transcript</DialogTitle>
                    <DialogContent sx={{ whiteSpace: "pre-line", maxHeight: "60vh", overflow: "auto" }}>
                        {loadingTranscriptText ? <CircularProgress /> : <Typography>{transcriptText || "No transcript available."}</Typography>}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setTranscriptModalOpen(false)}>Close</Button>
                    </DialogActions>
                </Dialog>
            )}

            {/* Summary viewer modal */}
            {summaryModalOpen && (
                <Dialog
                    open={summaryModalOpen}
                    onClose={() => setSummaryModalOpen(false)}
                    fullWidth
                    maxWidth="lg"
                    PaperProps={{ style: { minHeight: "70vh" } }}
                >
                    <DialogTitle sx={{ backgroundColor: "#0B194A", color: "white", position: "relative" }}>
                        <Typography variant="h6">Audio Summary</Typography>
                        <IconButton
                            onClick={() => setSummaryModalOpen(false)}
                            sx={{ position: "absolute", top: 8, right: 8, color: "white" }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent sx={{ pt: 3 }}>
                        {currentSummaries.length === 0 ? (
                            <Box textAlign="center" py={4}>
                                <Typography variant="h6" color="textSecondary">
                                    No summaries available.
                                </Typography>
                            </Box>
                        ) : (
                            <Box>
                                {currentSummaries.map((summary, index) => (
                                    <Paper key={summary.uuid || index} elevation={2} sx={{ mb: 2, p: 2 }}>
                                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                                            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                                                {summary.name}
                                            </Typography>

                                            <Box display="flex" gap={1} flexWrap="wrap">
                                                {summary.categories && summary.categories.length > 0
                                                    ? summary.categories.map((cat) => (
                                                        <Box
                                                            key={cat.id}
                                                            sx={{
                                                                bgcolor: getCategoryColor(cat.name),
                                                                color: "white",
                                                                px: 2,
                                                                py: 0.5,
                                                                borderRadius: "16px",
                                                                fontSize: "12px",
                                                                fontWeight: "bold",
                                                            }}
                                                        >
                                                            {cat.name}
                                                        </Box>
                                                    ))
                                                    : (
                                                        <Box
                                                            sx={{
                                                                bgcolor: "#9E9E9E",
                                                                color: "white",
                                                                px: 2,
                                                                py: 0.5,
                                                                borderRadius: "16px",
                                                                fontSize: "12px",
                                                                fontWeight: "bold",
                                                            }}
                                                        >
                                                            Uncategorized
                                                        </Box>
                                                    )}
                                            </Box>
                                        </Box>

                                        <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                                            <strong>Short Summary:</strong> {summary.short_summary}
                                        </Typography>

                                        <Box sx={{ maxHeight: "200px", overflowY: "auto", mb: 2 }}>
                                            <Typography variant="body2" sx={{ lineHeight: 1.6, whiteSpace: "pre-line" }}>
                                                <strong>Long Summary:</strong> {summary.long_summary}
                                            </Typography>
                                        </Box>

                                        <Box display="flex" justifyContent="space-between" alignItems="center">
                                            <Typography variant="caption" color="textSecondary">
                                                {summary.state}, {summary.county}, {summary.municipality}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {new Date(summary.inserted_at).toLocaleDateString()}
                                            </Typography>
                                        </Box>
                                    </Paper>
                                ))}
                            </Box>
                        )}
                    </DialogContent>
                </Dialog>
            )}
            <Dialog open={confirmDialogOpen} onClose={handleConfirmDialogClose}>
                <DialogTitle>Confirm Action</DialogTitle>
                <DialogContent>
                    <Typography>{confirmDialogMessage}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleConfirmDialogClose}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={handleConfirmDialogConfirm}>
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>

        </Dialog>
    );
};

export default EventPropertiesModal;



// const EventPropertiesModal = ({ open, onClose, event, fetchEvents, getToken }) => {
//   const [audioList, setAudioList] = useState([]);
//   const [selectedAudioIndex, setSelectedAudioIndex] = useState(null);
//   const [isPlayingIndex, setIsPlayingIndex] = useState(null);
//   const [loadingAudioList, setLoadingAudioList] = useState(false);

//   const [transcriptLoadingMap, setTranscriptLoadingMap] = useState({});
//   const [transcriptPollingMap, setTranscriptPollingMap] = useState({});
//   const [summaryLoadingMap, setSummaryLoadingMap] = useState({});
//   const [summariesMap, setSummariesMap] = useState({});

//   const [transcriptModalOpen, setTranscriptModalOpen] = useState(false);
//   const [transcriptText, setTranscriptText] = useState("");
//   const [loadingTranscriptText, setLoadingTranscriptText] = useState(false);

//   const [summaryModalOpen, setSummaryModalOpen] = useState(false);
//   const [currentSummaries, setCurrentSummaries] = useState([]);
//   const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
//   const [confirmDialogMessage, setConfirmDialogMessage] = useState("");
//   const [confirmDialogAction, setConfirmDialogAction] = useState(() => () => {});
//   const [deleteLoading, setDeleteLoading] = useState(null);

//   const audioRef = useRef();

//   const getHeaders = () => ({
//     Authorization: `Bearer ${getToken()}`,
//     "Content-Type": "application/json",
//   });

//   // Fetch audio files when modal opens
//   useEffect(() => {
//     if (event && open) {
//       fetchAudioFiles(event.id);
//     } else {
//       setAudioList([]);
//       setSelectedAudioIndex(null);
//       setIsPlayingIndex(null);
//       setTranscriptLoadingMap({});
//       setSummaryLoadingMap({});
//       setSummariesMap({});
//       setTranscriptPollingMap({});
//       setLoadingAudioList(false);
//     }
//   }, [event, open]);

//   // Preload metadata for all audio files
//   useEffect(() => {
//     if (audioList.length > 0) {
//       audioList.forEach((a) => {
//         const url = getAudioUrl(a.file);
//         if (url) {
//           const audio = new Audio(url);
//           audio.preload = "metadata";
//         }
//       });
//     }
//   }, [audioList]);

//   // Helper: Construct audio URL
//   const getAudioUrl = (filePath) => {
//     if (!filePath) return null;
//     if (filePath.startsWith("http")) return filePath;
//     return `${BACKEND_BASE_URL}${filePath}`;
//   };

//   // Fetch audio files from backend
//   const fetchAudioFiles = async (eventId) => {
//     setLoadingAudioList(true);
//     try {
//       const res = await fetch(API_AUDIO_LIST(eventId), { headers: getHeaders() });
//       if (!res.ok) {
//         toast.error("Failed to fetch audio files.");
//         setAudioList([]);
//         return;
//       }
//       const data = await res.json();
//       const list = Array.isArray(data.data) ? data.data : Array.isArray(data) ? data : [];
//       setAudioList(list);
//     } catch (err) {
//       console.error(err);
//       toast.error("Error fetching audio files.");
//       setAudioList([]);
//     } finally {
//       setLoadingAudioList(false);
//     }
//   };

//   // ✅ Optimized playback logic
//   const playAudio = (index) => {
//     const audio = audioList[index];
//     if (!audio) return;

//     const url = getAudioUrl(audio.file);
//     if (!url) {
//       toast.error("Audio URL missing.");
//       return;
//     }

//     if (!audioRef.current) return;

//     // Same audio: toggle play/pause
//     if (isPlayingIndex === index) {
//       audioRef.current.pause();
//       setIsPlayingIndex(null);
//       return;
//     }

//     // Switch to a new audio
//     try {
//       audioRef.current.pause();
//       audioRef.current.src = url;
//       const playPromise = audioRef.current.play();
//       if (playPromise && playPromise.catch) {
//         playPromise
//           .then(() => setIsPlayingIndex(index))
//           .catch(() => toast.error("Failed to play audio."));
//       } else {
//         setIsPlayingIndex(index);
//       }
//       setSelectedAudioIndex(index);
//     } catch (err) {
//       console.error("Audio play error:", err);
//       toast.error("Error playing audio.");
//     }
//   };

//   const handleAudioEnded = () => setIsPlayingIndex(null);

//   // ------------------- Transcript & Summary Functions -------------------
//   const generateTranscript = async (audio) => {
//     if (!audio || !audio.id) return;
//     const audioId = audio.id;

//     if (transcriptLoadingMap[audioId]) {
//       toast.info("Transcript already processing...");
//       startTranscriptPolling(audioId);
//       return;
//     }

//     setTranscriptLoadingMap((m) => ({ ...m, [audioId]: true }));

//     try {
//       const res = await fetch(API_AUDIO_PROCESS(audioId), {
//         method: "POST",
//         headers: getHeaders(),
//       });
//       const data = await res.json();

//       if (res.ok && (data.status === "started" || data.status === "processing")) {
//         toast.info("Transcription started. Polling for updates...");
//         startTranscriptPolling(audioId);
//       } else if (data.status === "exists") {
//         toast.info("Transcript already exists; fetching it.");
//         await fetchTranscriptAndUpdate(audio.event, audioId);
//       } else {
//         toast.error(data.message || "Failed to start transcription.");
//       }
//     } catch (err) {
//       console.error(err);
//       toast.error("Error initiating transcript.");
//     } finally {
//       setTranscriptLoadingMap((m) => ({ ...m, [audioId]: false }));
//     }
//   };

//   const startTranscriptPolling = (audioId, interval = 3000, maxAttempts = 60) => {
//     if (transcriptPollingMap[audioId]) return;
//     setTranscriptPollingMap((m) => ({ ...m, [audioId]: true }));

//     let attempts = 0;
//     const poll = async () => {
//       attempts++;
//       try {
//         const transcriptRes = await fetch(API_TRANSCRIPT_DETAIL(event.id, audioId), {
//           headers: getHeaders(),
//         });
//         if (transcriptRes.ok) {
//           const json = await transcriptRes.json();
//           const transcriptData = json.data || json;
//           if (transcriptData?.raw_text) {
//             await fetchAudioFiles(event.id);
//             setTranscriptPollingMap((m) => ({ ...m, [audioId]: false }));
//             toast.success("Transcript completed.");
//             return;
//           }
//         }
//       } catch (err) {
//         console.error("Transcript poll error", err);
//       }

//       if (attempts < maxAttempts) {
//         setTimeout(poll, interval);
//       } else {
//         toast.warning("Transcript taking longer than expected.");
//         setTranscriptPollingMap((m) => ({ ...m, [audioId]: false }));
//       }
//     };
//     poll();
//   };

//   const fetchTranscriptAndUpdate = async (eventId, audioId) => {
//     setLoadingTranscriptText(true);
//     try {
//       const res = await fetch(API_TRANSCRIPT_DETAIL(eventId, audioId), {
//         headers: getHeaders(),
//       });
//       if (res.ok) {
//         const data = await res.json();
//         const txt = data.data?.raw_text || "No transcript found.";
//         setTranscriptText(txt);
//         setTranscriptModalOpen(true);
//       } else {
//         toast.error("Failed to fetch transcript.");
//       }
//     } catch (err) {
//       console.error(err);
//       toast.error("Error fetching transcript.");
//     } finally {
//       setLoadingTranscriptText(false);
//     }
//   };

//   const viewTranscript = async (audio) => {
//     if (!audio) return;
//     await fetchTranscriptAndUpdate(event.id, audio.id);
//   };

//   const generateSummary = async (audio) => {
//     if (!audio) return [];
//     const audioId = audio.id;
//     const transcript = audio.transcripts?.[0];
//     if (!transcript) {
//       toast.warning("No transcript available. Generate transcript first.");
//       return [];
//     }

//     const transcriptId = transcript.id;
//     setSummaryLoadingMap((m) => ({ ...m, [audioId]: true }));

//     try {
//       const res = await fetch(API_AUDIO_SUMMARY_GENERATE, {
//         method: "POST",
//         headers: getHeaders(),
//         body: JSON.stringify({
//           capture_transcript_id: transcriptId,
//           event_id: event.id,
//           state: event?.state || "",
//           county: event?.county || "",
//           municipality: event?.municipality_name || "",
//         }),
//       });
//       const data = await res.json();
//       if (res.ok && data.status === "completed") {
//         const summaries = data.summaries || [];
//         setSummariesMap((m) => ({ ...m, [audioId]: summaries }));
//         toast.success("Summary generated successfully.");
//         fetchEvents?.();
//         return summaries;
//       } else {
//         toast.error(data.message || "Failed to generate summary");
//         return [];
//       }
//     } catch (err) {
//       console.error("Summary generation error", err);
//       toast.error("Error generating summary.");
//       return [];
//     } finally {
//       setSummaryLoadingMap((m) => ({ ...m, [audioId]: false }));
//     }
//   };

//   const viewSummary = async (audio) => {
//     if (!audio) return;
//     const audioId = audio.id;
//     if (summariesMap[audioId]?.length > 0) {
//       showSummariesDialog(summariesMap[audioId]);
//       return;
//     }
//     const summaries = await generateSummary(audio);
//     if (summaries?.length > 0) showSummariesDialog(summaries);
//     else toast.info("No summaries available.");
//   };

//   const showSummariesDialog = (summaries) => {
//     setCurrentSummaries(summaries);
//     setSummaryModalOpen(true);
//   };

//   const handleAudioDelete = (id) => {
//     showConfirmDialog("Are you sure you want to delete this event?", () =>
//       handleDeleteConfirmed(id)
//     );
//   };

//   const API_DELETE_EVENT = (id) =>
//     `${BACKEND_BASE_URL}/api/capture/capture-events/delete/${id}/`;

//   const handleDeleteConfirmed = async (id) => {
//     setDeleteLoading(id);
//     try {
//       const res = await fetch(API_DELETE_EVENT(id), {
//         method: "DELETE",
//         headers: getHeaders(),
//       });
//       if (res.ok) {
//         toast.success("Event deleted successfully.");
//         fetchEvents?.();
//         onClose();
//       } else {
//         toast.error("Failed to delete event.");
//       }
//     } catch (err) {
//       console.error(err);
//       toast.error("Error deleting event.");
//     } finally {
//       setDeleteLoading(null);
//     }
//   };

//   const showConfirmDialog = (message, onConfirm) => {
//     setConfirmDialogMessage(message);
//     setConfirmDialogAction(() => onConfirm);
//     setConfirmDialogOpen(true);
//   };

//   const handleConfirmDialogClose = () => setConfirmDialogOpen(false);
//   const handleConfirmDialogConfirm = () => {
//     confirmDialogAction();
//     handleConfirmDialogClose();
//   };

//   // ------------------- Render -------------------
//   return (
//     <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" PaperProps={{ style: { minHeight: "70vh" } }}>
//       <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
//         <Typography variant="h6">Properties - {event?.title}</Typography>
//         <IconButton onClick={onClose}>
//           <CloseIcon />
//         </IconButton>
//       </DialogTitle>

//       <DialogContent>
//         {!event ? (
//           <Typography>No event selected.</Typography>
//         ) : loadingAudioList ? (
//           <Box textAlign="center" py={6}>
//             <CircularProgress size={50} />
//             <Typography sx={{ mt: 2 }}>Loading audio files...</Typography>
//           </Box>
//         ) : audioList.length === 0 ? (
//           <Box textAlign="center" py={6}>
//             <Typography>No audio files available.</Typography>
//           </Box>
//         ) : (
//           audioList.map((audio, idx) => (
//             <Paper key={audio.id} sx={{ mb: 2, p: 2 }}>
//               <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
//                 <Box flex="1">
//                   <ListItem disableGutters sx={{ p: 0 }}>
//                     <ListItemText
//                       primary={audio.caption || `Audio ${idx + 1}`}
//                       secondary={`Uploaded at: ${new Date(audio.uploaded_at).toLocaleString()}`}
//                       primaryTypographyProps={{ fontWeight: "bold" }}
//                     />
//                   </ListItem>

//                   {audio.transcripts?.[0]?.raw_text && (
//                     <Typography variant="body2" sx={{ mt: 1, maxHeight: 80, overflow: "auto", whiteSpace: "pre-line" }}>
//                       {audio.transcripts[0].raw_text.slice(0, 300)}
//                       {audio.transcripts[0].raw_text.length > 300 ? "..." : ""}
//                     </Typography>
//                   )}
//                 </Box>

//                 <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
//                   <Button
//                     variant={isPlayingIndex === idx ? "contained" : "outlined"}
//                     color={isPlayingIndex === idx ? "success" : "primary"}
//                     size="small"
//                     startIcon={isPlayingIndex === idx ? <PauseIcon /> : <PlayArrowIcon />}
//                     onClick={() => playAudio(idx)}
//                   >
//                     {isPlayingIndex === idx ? "Pause" : "Play"}
//                   </Button>

//                   <Button
//                     variant="contained"
//                     size="small"
//                     onClick={() => generateTranscript(audio)}
//                     disabled={transcriptLoadingMap[audio.id]}
//                   >
//                     {transcriptLoadingMap[audio.id] ? <CircularProgress size={18} /> : "Generate Transcript"}
//                   </Button>

//                   <Button
//                     variant="contained"
//                     size="small"
//                     onClick={() => generateSummary(audio)}
//                     disabled={summaryLoadingMap[audio.id]}
//                   >
//                     {summaryLoadingMap[audio.id] ? <CircularProgress size={18} /> : "Generate Summary"}
//                   </Button>

//                   <Button variant="outlined" size="small" onClick={() => viewTranscript(audio)}>
//                     View Transcript
//                   </Button>

//                   <Button variant="outlined" size="small" onClick={() => viewSummary(audio)}>
//                     View Summary
//                   </Button>
//                 </Box>
//               </Box>
//             </Paper>
//           ))
//         )}

//         {/* ✅ Single persistent audio player */}
//         <Box sx={{ mt: 2 }}>
//           <audio ref={audioRef} onEnded={handleAudioEnded} preload="metadata" controls style={{ width: "100%" }} />
//         </Box>
//       </DialogContent>

//       <DialogActions>
//         <Button color="error" variant="contained" onClick={() => handleAudioDelete(event?.id)} disabled={deleteLoading === event?.id}>
//           {deleteLoading === event?.id ? <CircularProgress size={20} /> : "Delete Event"}
//         </Button>
//       </DialogActions>

//       {/* Transcript Modal */}
//       <Dialog open={transcriptModalOpen} onClose={() => setTranscriptModalOpen(false)} fullWidth maxWidth="md">
//         <DialogTitle>Transcript</DialogTitle>
//         <DialogContent sx={{ whiteSpace: "pre-line", maxHeight: "60vh", overflow: "auto" }}>
//           {loadingTranscriptText ? <CircularProgress /> : <Typography>{transcriptText || "No transcript available."}</Typography>}
//         </DialogContent>
//         <DialogActions>
//           <Button onClick={() => setTranscriptModalOpen(false)}>Close</Button>
//         </DialogActions>
//       </Dialog>

//       {/* Summary Modal */}
//       <Dialog open={summaryModalOpen} onClose={() => setSummaryModalOpen(false)} fullWidth maxWidth="lg">
//         <DialogTitle sx={{ backgroundColor: "#0B194A", color: "white" }}>
//           <Typography variant="h6">Audio Summary</Typography>
//           <IconButton
//             onClick={() => setSummaryModalOpen(false)}
//             sx={{ position: "absolute", top: 8, right: 8, color: "white" }}
//           >
//             <CloseIcon />
//           </IconButton>
//         </DialogTitle>
//         <DialogContent sx={{ pt: 3 }}>
//           {currentSummaries.length === 0 ? (
//             <Box textAlign="center" py={4}>
//               <Typography variant="h6" color="textSecondary">
//                 No summaries available.
//               </Typography>
//             </Box>
//           ) : (
//             currentSummaries.map((summary, index) => (
//               <Paper key={summary.uuid || index} elevation={2} sx={{ mb: 2, p: 2 }}>
//                 <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
//                   {summary.name}
//                 </Typography>
//                 <Typography variant="body1" sx={{ mb: 2 }}>
//                   <strong>Short Summary:</strong> {summary.short_summary}
//                 </Typography>
//                 <Typography variant="body2" sx={{ mb: 2 }}>
//                   <strong>Long Summary:</strong> {summary.long_summary}
//                 </Typography>
//               </Paper>
//             ))
//           )}
//         </DialogContent>
//       </Dialog>

//       {/* Confirm Dialog */}
//       <Dialog open={confirmDialogOpen} onClose={handleConfirmDialogClose}>
//         <DialogTitle>Confirm Action</DialogTitle>
//         <DialogContent>
//           <Typography>{confirmDialogMessage}</Typography>
//         </DialogContent>
//         <DialogActions>
//           <Button onClick={handleConfirmDialogClose}>Cancel</Button>
//           <Button color="error" variant="contained" onClick={handleConfirmDialogConfirm}>
//             Confirm
//           </Button>
//         </DialogActions>
//       </Dialog>
//     </Dialog>
//   );
// };

// export default EventPropertiesModal
